import { z } from 'zod';
const Uuid = z.string().uuid();
const OpaqueSourceReference = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{2,199}$/);
const IsoTimestamp = z.string().datetime();
const FictionalSafeDisplayMessage = z.string().trim().min(1).max(280)
    .refine((value) => !/[<>&]/.test(value) && [...value].every((character) => character >= ' ' && character !== '\u007F'), 'Display text must not contain markup or control characters.')
    .refine((value) => !/\b(password|token|secret|bearer|api[ _-]?key|prescription|diagnos(?:is|es|tic)?|patient|medical|health|symptom|dosage|medication|drug|allerg(?:y|ies))\b/i.test(value), 'Display text is not permitted for this fictional-safe channel.');
export const CommunicationsSourceKindSchema = z.enum(['WEBSITE', 'WOOCOMMERCE', 'BCONNECT', 'WHATSAPP', 'INSTAGRAM', 'FACEBOOK']);
export const StrongIdentifierKindSchema = z.enum(['PHONE', 'EMAIL']);
export const ConversationStatusSchema = z.enum(['UNHANDLED', 'HANDLED', 'CLOSED']);
export const SyntheticInboundSchema = z.object({
    organizationId: Uuid,
    branchId: Uuid.nullable(),
    datasetId: Uuid,
    sourceKind: z.enum(['WOOCOMMERCE', 'BCONNECT', 'WHATSAPP', 'INSTAGRAM', 'FACEBOOK']),
    sourceConversationKey: OpaqueSourceReference,
    sourceEventId: OpaqueSourceReference,
    idempotencyKey: OpaqueSourceReference,
    eventKind: z.enum(['INBOUND_MESSAGE', 'INBOUND_EVENT']).default('INBOUND_MESSAGE'),
    displayMessage: FictionalSafeDisplayMessage,
    sourceOccurredAt: IsoTimestamp,
    receivedAt: IsoTimestamp,
    identifierKind: StrongIdentifierKindSchema,
    identifierValue: z.string().trim().min(3).max(254),
}).strict();
export const StartSyntheticSessionSchema = z.object({
    organizationId: Uuid,
    branchId: Uuid.nullable(),
    datasetId: Uuid,
    anonymousIdentityKey: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{7,199}$/),
    sourceSessionKey: OpaqueSourceReference,
    sourceEventId: OpaqueSourceReference,
    startedAt: IsoTimestamp,
}).strict();
export const RecordFirstPartyEventSchema = z.object({
    sourceEventId: OpaqueSourceReference,
    eventName: z.enum([
        'session_started', 'page_viewed', 'category_viewed', 'product_viewed', 'search_performed',
        'add_to_cart', 'remove_from_cart', 'cart_viewed', 'checkout_started', 'checkout_progressed',
        'order_created', 'order_completed', 'order_failed', 'order_cancelled', 'refund_created',
    ]),
    occurredAt: IsoTimestamp,
}).strict();
export const ExactSessionIdentitySchema = z.object({
    identifierKind: StrongIdentifierKindSchema,
    identifierValue: z.string().trim().min(3).max(254),
}).strict();
export const ConversationStatusUpdateSchema = z.object({ status: z.enum(['HANDLED', 'CLOSED']) }).strict();
export const ConflictReviewSchema = z.object({ customerId: Uuid.optional(), reject: z.boolean().default(false) }).strict()
    .superRefine((value, ctx) => { if (!value.reject && !value.customerId)
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A selected customer is required to resolve a conflict.' }); });
export const OrganizationQueueQuerySchema = z.object({ organizationId: Uuid }).strict();
export const UnhandledConversationSchema = z.object({
    conversationId: Uuid,
    branchId: Uuid.nullable(),
    customerId: Uuid.nullable(),
    leadId: Uuid.nullable(),
    sourceKind: CommunicationsSourceKindSchema,
    displayMessage: FictionalSafeDisplayMessage,
    lastReceivedAt: z.string(),
}).strict();
export const UnhandledConversationResponseSchema = z.object({ success: z.literal(true), data: z.array(UnhandledConversationSchema).max(100) }).strict();
export const OpenIdentityConflictSchema = z.object({
    conflictId: Uuid,
    existingCustomerId: Uuid,
    candidateCustomerId: Uuid,
}).strict();
export const OpenIdentityConflictResponseSchema = z.object({ success: z.literal(true), data: z.array(OpenIdentityConflictSchema).max(100) }).strict();
export const CustomerJourneyItemSchema = z.object({
    recordId: Uuid,
    source: z.enum(['COMMUNICATIONS', 'WEBSITE', 'CRM']),
    observationType: z.enum(['DIRECTLY_OBSERVED', 'CRM_OPERATIONAL']),
    occurredAt: z.string(),
    branchId: Uuid.nullable(),
    eventType: z.string().min(1).max(127),
}).strict();
export const CustomerJourneyResponseSchema = z.object({ success: z.literal(true), data: z.array(CustomerJourneyItemSchema).max(200) }).strict();
export const CreatedIdResponseSchema = z.object({ success: z.literal(true), data: z.object({ id: Uuid }).strict() }).strict();
//# sourceMappingURL=communications.js.map